import { findIndex, isEqual, some } from "lodash"

type Comparison<T> = (a: T, b: T) => boolean

// Converts a list of items to a record by selecting one property
// as a key and another as a value.
export function listToRecord<T, K extends string | number, V>(
  items: T[],
  keySelector: (item: T) => K,
  valueSelector: (item: T) => V,
): Record<K, V> {
  return Object.assign(
    {},
    ...items.map(item => ({
      [keySelector(item)]: valueSelector(item),
    })),
  )
}

// Returns a copy of the list where an item was added at the end.
// If the list already contains the item, it will be swapped out
// with the new item instance at the old index position.
export function addOrReplaceInList<T>(
  list: T[],
  item: T,
  isEqual?: Comparison<T>,
): T[] {
  const newList: T[] = []
  let found = false
  for (const itemInList of list) {
    if (isEqual ? isEqual(itemInList, item) : itemInList == item) {
      newList.push(item)
      found = true
    } else newList.push(itemInList)
  }

  if (!found) newList.push(item)

  return newList
}

// Returns a copy of the list where an item was added at the end.
// If the list already contains the item, it will be swapped out
// with the result of the merge function.
export function addOrMergeInList<T>(
  list: T[],
  item: T,
  merge: (itemInList: T, item: T) => T,
  isEqual?: Comparison<T>,
): T[] {
  const newList: T[] = []
  let found = false
  for (const itemInList of list) {
    if (isEqual ? isEqual(itemInList, item) : itemInList == item) {
      newList.push(merge(itemInList, item))
      found = true
    } else newList.push(itemInList)
  }

  if (!found) newList.push(item)

  return newList
}

// Returns a copy of the list where
// every instance of the item is removed.
export function removeFromList<T>(
  list: T[],
  item: T,
  isEqual?: Comparison<T>,
): T[] {
  const newList: T[] = []
  for (const itemInList of list) {
    if (isEqual ? isEqual(itemInList, item) : itemInList == item) {
      // omit
    } else newList.push(itemInList)
  }
  return newList
}

// Creates a new object from an existing one,
// only including the received keys.
export function pick<T, K extends keyof T>(input: T, keys: K[]): Pick<T, K> {
  const output = {} as Pick<T, K>
  for (const key of keys) output[key] = input[key]
  return output
}

// Works similarly to the lodash version of uniqBy,
// but it will keep the last occurrence of an item - not the first.
// However, it will still keep the same order of occurrence.
export function uniqByLast<K, V>(items: V[], keySelector: (item: V) => K): V[] {
  const map = new Map<K, V>()

  for (const item of items) map.set(keySelector(item), item)

  return Array.from(map.values())
}

// Returns the index of an item if it is part of an array.
// Otherwise, it returns -1.
export function indexOf<T>(array: T[] | undefined, item: T): number {
  return findIndex(array, arrayItem => isEqual(arrayItem, item))
}

// Returns true if an item of the array is equal to the one provided.
export function contains<T>(array: T[] | undefined, item: T): boolean {
  return some(array, arrayItem => isEqual(arrayItem, item))
}

// Returns all items from array `a`
// that are not present in array `b`.
// This behavior differs from lodash's `difference` function
// because duplicates are removed.
export function notIn<T>(a: T[] | undefined, b: T[] | undefined): T[] {
  if (a == undefined || a.length == 0) return []

  const setA = [...new Set(a)]
  if (b == undefined || b.length == 0) return setA

  const setB = new Set(b)
  return setA.filter(item => !setB.has(item))
}

// Returns true if both arrays contain
// items that are equal by a custom equality checker.
export function arrayEquals<T>(
  a: T[] | undefined,
  b: T[] | undefined,
  itemEquals: Comparison<T>,
): boolean {
  if (a == undefined && b == undefined) return true
  if (a == undefined || b == undefined) return false
  if (a.length != b.length) return false
  for (let i = 0; i < a.length; i++) if (!itemEquals(a[i], b[i])) return false
  return true
}
